/**
 * WebAuthn ceremony helpers.
 *
 * Root cause of "first register fails, second works": overlapping create()
 * (double-click / re-entry) aborts the first prompt → generic failure, then
 * the second ceremony succeeds. Guard with a sync lock; never abort an
 * in-flight platform prompt from a second click.
 */
import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser'

export class WebAuthnUserError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.code = code
  }
}

let ceremonyLock = false

export function mapWebAuthnError(err: unknown): WebAuthnUserError {
  if (err instanceof WebAuthnUserError) return err
  const name =
    err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : ''
  const msg =
    err && typeof err === 'object' && 'message' in err
      ? String((err as { message: string }).message)
      : String(err || 'unknown')

  if (name === 'NotAllowedError') {
    return new WebAuthnUserError(
      'cancelled',
      'Passkey prompt was cancelled or timed out — try again.',
    )
  }
  if (name === 'AbortError') {
    return new WebAuthnUserError(
      'cancelled',
      'Passkey prompt was interrupted — try again.',
    )
  }
  if (name === 'InvalidStateError') {
    return new WebAuthnUserError(
      'already_registered',
      'A passkey for this account already exists on this authenticator. Sign in with it instead.',
    )
  }
  if (name === 'NotSupportedError' || name === 'SecurityError') {
    return new WebAuthnUserError('unsupported', msg || 'Passkeys are not available here.')
  }
  if (/already pending/i.test(msg)) {
    return new WebAuthnUserError(
      'pending',
      'Another passkey prompt is still open — finish or close it, then try once.',
    )
  }
  return new WebAuthnUserError('failed', msg || 'Passkey ceremony failed')
}

async function withCeremonyLock<T>(run: () => Promise<T>): Promise<T> {
  if (ceremonyLock) {
    throw new WebAuthnUserError(
      'pending',
      'Passkey prompt already in progress — finish it or wait a moment.',
    )
  }
  ceremonyLock = true
  try {
    return await run()
  } catch (err) {
    throw mapWebAuthnError(err)
  } finally {
    ceremonyLock = false
  }
}

export async function ceremonyRegister(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  return withCeremonyLock(async () => {
    return (await startRegistration({ optionsJSON: options })) as RegistrationResponseJSON
  })
}

export async function ceremonyAuthenticate(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  return withCeremonyLock(async () => {
    return (await startAuthentication({ optionsJSON: options })) as AuthenticationResponseJSON
  })
}

export { browserSupportsWebAuthn }
